<script setup lang="ts">
import { computed, ref, nextTick, watch } from 'vue';
import { useRoute } from 'vue-router';
import { Swiper, SwiperSlide } from 'swiper/vue';
import {
  getProviderImage as getProviderImageUtil,
  getProviderStyle,
  type ProviderItem,
} from '@/utils/providerImages';
import 'swiper/css';

interface CategoryItem {
  slug: string;
  name: string;
  games: number;
}

// Simple debounce function
const debounce = (fn: Function, delay: number) => {
  let timeoutId: ReturnType<typeof setTimeout>;

  return (...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

// Ref for Swiper instance (providers)
const providersSwiper = ref<any>(null);

// Ref for sort dropdown
const isSortDropdownOpen = ref(false);
const sortDropdownRef = ref<HTMLElement | null>(null);

// Toggle sort dropdown
const toggleSortDropdown = () => {
  isSortDropdownOpen.value = !isSortDropdownOpen.value;
};

// Close dropdown when clicking outside
const closeSortDropdown = (event: MouseEvent) => {
  if (
    sortDropdownRef.value &&
    !sortDropdownRef.value.contains(event.target as Node)
  ) {
    isSortDropdownOpen.value = false;
  }
};

// Toggle sort direction
const toggleSortDirection = () => {
  const newDir = props.selectedSortDir === 'asc' ? 'desc' : 'asc';

  emit('update:selectedSortDir', newDir);
};

// Lifecycle - add click listener for closing dropdown
if (typeof window !== 'undefined') {
  watch(isSortDropdownOpen, (isOpen) => {
    if (isOpen) {
      document.addEventListener('click', closeSortDropdown);
    } else {
      document.removeEventListener('click', closeSortDropdown);
    }
  });
}

const onProvidersSwiper = (s: any) => {
  providersSwiper.value = s;
};

const scrollProvidersToStart = async () => {
  try {
    await nextTick();
    // jump immediately to first slide
    providersSwiper.value?.slideTo?.(0, 0);
  } catch (e) {
    // noop
  }
};

// Scroll to selected provider
const scrollToSelectedProvider = async (specificSlug?: string) => {
  try {
    await nextTick();
    if (!providersSwiper.value) return;

    // Use specific slug if provided, otherwise use last selected
    const selectedSlug =
      specificSlug ||
      props.selectedProviders[props.selectedProviders.length - 1];

    if (!selectedSlug) return;

    const index = filteredProvidersList.value.findIndex(
      (prov: any) => prov.slug === selectedSlug,
    );

    if (index >= 0) {
      // Add 1 because the first slide is "Provedoras"
      providersSwiper.value?.slideTo?.(index + 1, 300);
    }
  } catch (e) {
    // noop
  }
};

const props = defineProps({
  isSearchActive: {
    type: Boolean,
    required: true,
  },
  isFiltersActive: {
    type: Boolean,
    required: true,
  },
  isMobile: {
    type: Boolean,
    required: true,
  },
  searchQuery: {
    type: String,
    required: true,
  },
  selectedCategories: {
    type: Array as PropType<string[]>,
    required: true,
  },
  selectedProviders: {
    type: Array as PropType<string[]>,
    required: true,
  },
  categoriesList: {
    type: Array as PropType<CategoryItem[]>,
    default: () => [],
  },
  selectedTypes: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  selectedSortBy: {
    type: String,
    default: '',
  },
  selectedSortDir: {
    type: String,
    default: '',
  },
  typesList: {
    type: Array as PropType<GameFilterType[]>,
    default: () => [],
  },
  sortByList: {
    type: Array as PropType<GameSortOption[]>,
    default: () => [],
  },
  sortDirList: {
    type: Array as PropType<GameSortOption[]>,
    default: () => [],
  },
  isSortLocked: {
    type: Boolean,
    default: false,
  },
  // Note: providersList removido — agora usamos a lista estática interna
});

type Emits = {
  'update:searchQuery': [any];
  'update:selectedProviders': [any];
  'update:selectedCategories': [any];
  'update:selectedTypes': [any];
  'update:selectedSortBy': [any];
  'update:selectedSortDir': [any];
  _freeModeStaticRelease: [];
};

const emit = defineEmits<Emits>();

const debouncedUpdateSearchQuery = debounce((value: string) => {
  emit('update:searchQuery', value);
}, 300); // Debounce for 300ms

const clearSearch = () => {
  emit('update:searchQuery', '');
};

const route = useRoute();
const categorySlug = computed<string>(() => {
  const { category } = route.params;

  if (Array.isArray(category) && category.length > 0) {
    return String(category[0]);
  }

  return '';
});

const providerSlug = computed<string>(() => {
  const { provider } = route.params;

  if (typeof provider === 'string') {
    return provider;
  }

  return '';
});

const toggleProvider = (slug: string) => {
  const currentProviders = [...props.selectedProviders];
  const index = currentProviders.indexOf(slug);

  if (index > -1) {
    currentProviders.splice(index, 1);
  } else {
    currentProviders.push(slug);
  }

  // Emit the new selection. If after removal there are zero selected,
  // emit empty array (marks "Todos") and scroll to start.
  emit('update:selectedProviders', currentProviders);

  if (index > -1 && currentProviders.length === 0) {
    // last provider was deselected
    scrollProvidersToStart();
  } else if (index === -1) {
    // provider was added, scroll to it
    scrollToSelectedProvider(slug);
  }
};

const toggleCategory = (slug: string) => {
  const currentCategories = [...props.selectedCategories];
  const index = currentCategories.indexOf(slug);

  if (slug === 'todos') {
    emit('update:selectedCategories', ['todos']);

    return;
  }

  if (slug === 'mais-apostados') {
    if (props.selectedCategories.includes('mais-apostados')) {
      emit('update:selectedCategories', ['todos']);
    } else {
      emit('update:selectedCategories', ['mais-apostados']);
    }

    return;
  }

  if (index > -1) {
    emit('update:selectedCategories', ['todos']);
  } else {
    emit('update:selectedCategories', [slug]);
  }
};

const resetProviders = () => {
  emit('update:selectedProviders', []);
  scrollProvidersToStart();
};

const sortedCategoriesList = computed(() => {
  // Apenas categorias do backend (filtra "todos" e "mais-apostados"/legado caso venham)
  return (props.categoriesList ?? []).filter(
    (c) =>
      c.slug !== 'todos' &&
      c.slug !== 'mais-apostados' &&
      c.slug !== 'mais-pagos',
  );
});

const categoryTabs = computed(() => {
  const fixed = [
    { name: 'Todos', value: 'todos' },
    { name: 'Mais Apostados', value: 'mais-apostados' },
  ];

  const dynamic = sortedCategoriesList.value.map((c: any) => ({
    name: c.name,
    value: c.slug,
  }));

  return [...fixed, ...dynamic];
});

const activeCategoryValue = computed(() => {
  if (props.selectedCategories.length === 0) return 'todos';

  return props.selectedCategories[0];
});

function onCategoryTabSelect(value: string | number) {
  toggleCategory(String(value));
}

const getTextClasses = (isSelected: boolean) => {
  // Novo design: texto branco em ambos os estados
  const colorBase = 'text-white';
  const colorSelected = 'text-white';

  const size = 'text-[11.051px]';
  const leading = 'leading-[11.051px]';
  const weight = 'font-bold';

  const base = `${colorBase} ${size} ${weight} ${leading}`;
  const selected = `${colorSelected} ${size} ${weight} ${leading}`;

  return isSelected ? selected : base;
};

const getCategoryButtonClasses = (isSelected: boolean) => {
  // Estado base: fundo com variável de cor
  const baseClasses =
    'flex justify-center items-center gap-2.5 px-[28px] py-[10px] rounded-[12px] h-[40px] border border-white/10 bg-[#3F3F46]/40 relative cursor-pointer active:scale-95 transition-transform';

  // Estado selecionado: gradiente azul com borda e sombra (z-index alto para sombra ficar acima)
  const selectedClasses =
    'flex justify-center items-center gap-2.5 px-[28px] py-[10px] rounded-[12px] h-[40px] border border-[#1414DD] bg-gradient-to-r from-[rgba(20,20,221,0.7)] to-[rgba(20,20,221,0.2)] shadow-[0_10px_15px_-3px_rgba(0,112,243,0.4),0_4px_6px_-2px_rgba(0,0,0,0.05)] relative z-50 cursor-pointer active:scale-95 transition-transform';

  return isSelected ? selectedClasses : baseClasses;
};

// Providers ordenados pela API, fallback para STATIC_PROVIDERS
const { providers: orderedProviders } = useOrderedProviders();
const filteredProvidersList = computed<ProviderItem[]>(
  () => orderedProviders.value,
);

const getProviderImage = (opt: ProviderItem): string | undefined => {
  return getProviderImageUtil(opt.slug);
};

// Watch for changes in selected providers and scroll to them
watch(
  () => props.selectedProviders,
  () => {
    scrollToSelectedProvider();
  },
  { deep: true },
);
</script>

<template>
  <div class="flex flex-col items-center justify-start select-none mt-2 mb-4">
    <div
      v-if="isFiltersActive || !isMobile"
      class="w-full flex flex-col items-center p-0 gap-3 self-stretch lg:[background:#0D1323] lg:backdrop-blur-[10px] lg:p-4 lg:rounded-[10px] overflow-x-clip overflow-y-visible"
    >
      <!-- Horizontal Select for Categories -->
      <div
        v-if="
          !categorySlug ||
          categorySlug === 'all' ||
          categorySlug === 'todos' ||
          categorySlug === 'recomendados'
        "
        class="relative w-full px-0 lg:px-1 flex items-center gap-2"
      >
        <!-- Voltar (só mobile): pill [‹ Voltar] compartilhado. -->
        <BaseBackButton v-if="isMobile" />
        <div class="flex-1 min-w-0">
          <BaseFilterTabs
            :model-value="activeCategoryValue"
            size="small"
            :tabs="categoryTabs"
            @update:model-value="onCategoryTabSelect"
          />
        </div>
      </div>

      <!-- Divider between Categories and Providers -->
      <div
        v-if="
          (!categorySlug ||
            categorySlug === 'all' ||
            categorySlug === 'todos' ||
            categorySlug === 'recomendados') &&
          (!providerSlug || providerSlug === 'all')
        "
        class="w-full h-px bg-white/10"
      ></div>

      <!-- Horizontal Select for Providers -->
      <div
        v-if="
          !providerSlug ||
          providerSlug === 'all' ||
          categorySlug === 'recomendados'
        "
        class="relative w-full px-0 lg:px-1"
      >
        <Swiper
          :class="['w-full', $style.swiperWithShadow]"
          :free-mode="true"
          :slides-per-view="'auto'"
          :space-between="8"
          @swiper="onProvidersSwiper"
        >
          <SwiperSlide class="w-auto flex-shrink-0">
            <button
              :class="getCategoryButtonClasses(selectedProviders.length === 0)"
              @click="resetProviders"
            >
              <span :class="getTextClasses(selectedProviders.length === 0)">
                {{ 'Provedoras' }}
              </span>
            </button>
          </SwiperSlide>
          <SwiperSlide
            v-for="(opt, i) in filteredProvidersList as any"
            :key="i"
            class="w-auto flex-shrink-0"
          >
            <button
              :class="
                getCategoryButtonClasses(selectedProviders.includes(opt.slug))
              "
              @click="toggleProvider(opt.slug)"
            >
              <div
                v-if="getProviderImage(opt)"
                :class="$style.providerImageWrapper"
              >
                <NuxtImg
                  :alt="opt.slug"
                  :class="$style.providerImage"
                  :draggable="false"
                  :src="getProviderImage(opt)"
                  :style="getProviderStyle(opt.slug)"
                />
              </div>
              <span
                v-else
                :class="getTextClasses(selectedProviders.includes(opt.slug))"
              >
                {{ opt.slug }}
              </span>
            </button>
          </SwiperSlide>
        </Swiper>
      </div>
    </div>
    <div
      v-if="isSearchActive || !isMobile"
      class="mt-4 w-full"
    >
      <div class="flex items-center gap-2 w-full">
        <!-- Search Input -->
        <div
          class="relative flex-1 flex items-center"
          style="
            background: #e4e4e7;
            border: 2px solid #a1a1aa;
            border-radius: 14px;
            height: 36px;
            padding: 0 8px;
          "
        >
          <input
            class="w-full h-[36px] bg-transparent text-black font-extrabold text-[12px] px-2 placeholder:text-bg-primary/80 placeholder:text-[12px] placeholder:font-extrabold"
            placeholder="Procure jogos ou provedoras"
            :value="searchQuery"
            @input="
              debouncedUpdateSearchQuery(
                ($event.target as HTMLInputElement)?.value,
              )
            "
          />
          <NuxtIcon
            v-if="searchQuery"
            class="text-bg-primary/70 cursor-pointer text-sm w-5 p-0 m-0 ml-2"
            name="close"
            @click.prevent="clearSearch"
          />
          <NuxtIcon
            class="text-bg-primary cursor-pointer text-[18px] ml-2"
            filled
            name="apostatudo/search-lens"
          />
        </div>

        <!-- Sort Dropdown Button -->
        <div
          v-if="!isSortLocked"
          ref="sortDropdownRef"
          class="relative"
        >
          <button
            aria-label="Menu de ordenação"
            class="flex items-center justify-center w-[35px] h-[35px] bg-[#3F3F46]/40 rounded-[6px] text-white hover:bg-[#3F3F46]/60 active:scale-95 active:bg-[#3F3F46]/80 transition-transform cursor-pointer"
            @click.stop="toggleSortDropdown"
          >
            <NuxtIcon
              class="self-center"
              name="filter/bars"
            />
          </button>

          <!-- Dropdown Menu -->
          <div
            v-if="isSortDropdownOpen"
            class="absolute right-0 top-12 w-[160px] bg-[#0D1323] backdrop-blur-md rounded-[12px] shadow-xl z-50"
          >
            <!-- Triangle pointer -->
            <NuxtIcon
              class="absolute -top-[7px] right-2.5 w-4 h-4 text-[#0D1323]"
              name="filter/dropdown-arrow"
            />

            <div class="relative py-4 px-3 flex flex-col gap-2">
              <!-- Sem filtro option -->
              <button
                class="w-full px-3 py-2 text-center text-[12px] font-medium text-white rounded-[12px] bg-[#3F3F46]/40 hover:bg-[#3F3F46]/60 transition-colors"
                :class="{
                  'bg-gradient-to-r from-[rgba(20,20,221,0.7)] to-[rgba(20,20,221,0.2)] border border-[#1414DD]':
                    selectedSortBy === '',
                }"
                @click="
                  emit('update:selectedSortBy', '');
                  isSortDropdownOpen = false;
                "
              >
                Sem filtro
              </button>

              <button
                v-for="sort in sortByList"
                :key="sort.slug"
                class="w-full px-3 py-2 text-center text-[12px] font-medium text-white rounded-[12px] bg-[#3F3F46]/40 hover:bg-[#3F3F46]/60 transition-colors"
                :class="{
                  'bg-gradient-to-r from-[rgba(20,20,221,0.7)] to-[rgba(20,20,221,0.2)] border border-[#1414DD]':
                    selectedSortBy === sort.slug,
                }"
                @click="
                  emit('update:selectedSortBy', sort.slug);
                  isSortDropdownOpen = false;
                "
              >
                {{ sort.name }}
              </button>
            </div>
          </div>
        </div>

        <!-- Sort Direction Toggle Button -->
        <button
          v-if="!isSortLocked"
          aria-label="Alternar direção de ordenação"
          class="flex items-center justify-center w-[35px] h-[35px] bg-[#3F3F46]/40 rounded-[6px] text-white hover:bg-[#3F3F46]/60 active:scale-95 active:bg-[#3F3F46]/80 transition-transform cursor-pointer"
          @click.stop="toggleSortDirection"
        >
          <NuxtIcon
            class="self-center text-[20px]"
            :name="selectedSortDir === 'asc' ? 'filter/az' : 'filter/za'"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<style module lang="postcss">
.providerImage {
  /* Mantém a imagem dentro do wrapper, sem exceder dimensões. */
  @apply block max-w-full max-h-full object-contain;
}

.providerImageWrapper {
  /* Container com tamanho fixo que centraliza o conteúdo. */
  @apply w-8 h-6 flex items-center justify-center;
}

.swiperWithShadow {
  overflow: visible !important;

  :global(.swiper-wrapper) {
    overflow: visible !important;
  }
}
</style>
